import { supabase } from '@/integrations/supabase/client'
import type { Transaction } from '@/lib/types'
import { getCategoryIcon, getCategoryColor } from '@/lib/utils/category-helpers'

export interface TransactionTotals {
  income: number
  expenses: number
  balance: number
}

/**
 * Fetch all transactions for a user (expenses + incomes combined)
 */
export async function fetchTransactions(userId: string): Promise<Transaction[]> {
  console.log('📥 [SERVICE] fetchTransactions called', { userId })

  // Fetch expenses and incomes in parallel
  const [expensesResult, incomesResult] = await Promise.all([
    supabase
      .from('user_expenses')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false }),
    supabase
      .from('user_incomes')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
  ])

  if (expensesResult.error) {
    console.error('❌ [SERVICE] Error fetching expenses:', {
      error: expensesResult.error,
      message: expensesResult.error.message,
      code: expensesResult.error.code,
      details: expensesResult.error.details,
      hint: expensesResult.error.hint,
      userId
    })
    throw new Error(`Failed to fetch expenses: ${expensesResult.error.message}`)
  }

  if (incomesResult.error) {
    console.error('❌ [SERVICE] Error fetching incomes:', {
      error: incomesResult.error,
      message: incomesResult.error.message,
      code: incomesResult.error.code,
      details: incomesResult.error.details,
      hint: incomesResult.error.hint,
      userId
    })
    throw new Error(`Failed to fetch incomes: ${incomesResult.error.message}`)
  }

  console.log('✅ [SERVICE] Fetched data successfully', {
    expensesCount: expensesResult.data.length,
    incomesCount: incomesResult.data.length,
    userId
  })

  // Convert expenses to transactions (negative amounts)
  const expenseTransactions: Transaction[] = (expensesResult.data as any[]).map((expense: any) => ({
    id: expense.id,
    name: expense.name,
    category: expense.category,
    amount: -Math.abs(expense.amount), // Ensure negative
    date: new Date(expense.created_at).toISOString(),
    icon: getCategoryIcon(expense.category),
    color: getCategoryColor(expense.category),
    type: 'expense' as const,
    frequency: expense.frequency as Transaction['frequency'],
    subAccountId: expense.sub_account_id,
    paymentCardId: expense.payment_card_id
  }))

  // Convert incomes to transactions (positive amounts)
  const incomeTransactions: Transaction[] = (incomesResult.data as any[]).map((income: any) => ({
    id: income.id,
    name: income.name,
    category: income.category || 'Income',
    amount: Math.abs(income.amount), // Ensure positive
    date: new Date(income.created_at).toISOString(),
    icon: getCategoryIcon(income.category || 'Income'),
    color: getCategoryColor(income.category || 'Income'),
    type: 'income' as const,
    frequency: income.frequency as Transaction['frequency'],
    subAccountId: income.sub_account_id,
    paymentCardId: income.payment_card_id
  }))

  // Combine and sort by date descending (latest/newest first)
  // Note: DB queries already order by created_at DESC, but we sort again here
  // to ensure correct ordering after combining expenses and incomes
  const allTransactions = [...expenseTransactions, ...incomeTransactions]
  allTransactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  return allTransactions
}

/**
 * Fetch recent transactions (limit to N items)
 */
export async function fetchRecentTransactions(userId: string, limit: number = 10): Promise<Transaction[]> {
  const allTransactions = await fetchTransactions(userId)
  return allTransactions.slice(0, limit)
}

/**
 * Calculate totals for income and expenses
 */
export async function calculateTotals(userId: string): Promise<TransactionTotals> {
  console.log('🧮 [SERVICE] calculateTotals called', { userId })

  // Fetch all expenses and incomes
  const [expensesResult, incomesResult] = await Promise.all([
    supabase
      .from('user_expenses')
      .select('amount')
      .eq('user_id', userId),
    supabase
      .from('user_incomes')
      .select('amount')
      .eq('user_id', userId)
  ])

  if (expensesResult.error) {
    console.error('❌ [SERVICE] Error calculating expenses total:', {
      error: expensesResult.error,
      message: expensesResult.error.message,
      userId
    })
    throw new Error(`Failed to calculate expenses: ${expensesResult.error.message}`)
  }

  if (incomesResult.error) {
    console.error('❌ [SERVICE] Error calculating incomes total:', {
      error: incomesResult.error,
      message: incomesResult.error.message,
      userId
    })
    throw new Error(`Failed to calculate incomes: ${incomesResult.error.message}`)
  }

  // Calculate totals
  const expenses = expensesResult.data.reduce((sum, expense) => sum + Number(expense.amount), 0)
  const income = incomesResult.data.reduce((sum, inc) => sum + Number(inc.amount), 0)
  const balance = income - expenses

  return {
    income,
    expenses,
    balance
  }
}

/**
 * Get transactions grouped by month
 */
export interface TransactionsByMonth {
  [monthYear: string]: Transaction[]
}

export async function fetchTransactionsByMonth(userId: string): Promise<TransactionsByMonth> {
  const transactions = await fetchTransactions(userId)

  const grouped: TransactionsByMonth = {}

  transactions.forEach((transaction) => {
    const date = new Date(transaction.date)
    const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

    if (!grouped[monthYear]) {
      grouped[monthYear] = []
    }

    grouped[monthYear].push(transaction)
  })

  return grouped
}

/**
 * Format transaction date for display
 */
export function formatTransactionDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffTime = Math.abs(now.getTime() - date.getTime())
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays <= 7) return `${diffDays} days ago`

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Delete a transaction (expense or income)
 */
export async function deleteTransaction(id: string, type: 'expense' | 'income'): Promise<void> {
  const tableName = type === 'expense' ? 'user_expenses' : 'user_incomes'

  const { error } = await supabase
    .from(tableName)
    .delete()
    .eq('id', id)

  if (error) {
    console.error(`Error deleting ${type}:`, error)
    throw new Error(`Failed to delete ${type}`)
  }
}

/**
 * Update a transaction (expense or income)
 */
export async function updateTransaction(
  id: string,
  type: 'expense' | 'income',
  data: {
    name?: string
    amount?: number
    category?: string
    created_at?: string
  }
): Promise<void> {
  const tableName = type === 'expense' ? 'user_expenses' : 'user_incomes'

  // Build update data
  const updateData: any = {}
  if (data.name !== undefined) updateData.name = data.name
  if (data.amount !== undefined) updateData.amount = data.amount
  if (data.created_at !== undefined) updateData.created_at = data.created_at

  // Only add category for expenses (incomes table doesn't have this column)
  if (type === 'expense' && data.category !== undefined) {
    updateData.category = data.category
  }

  const { error } = await supabase
    .from(tableName)
    .update(updateData)
    .eq('id', id)

  if (error) {
    console.error(`Error updating ${type}:`, error)
    throw new Error(`Failed to update ${type}`)
  }
}
